/* ============================================================================
   205 Holdings — the API seam.

   `Api` is the whole contract between the UI and the backend. There are two
   implementations: `mockApi` (realistic figures, offline) and `httpApi` (real
   fetch against the Axum server at `https://205.ogital.ai`, which is live).

   **Swapping mock for HTTP touches this file only.** Screens import `api`
   and never know which one they got.

       npm run dev                   # the real server, the default
       VITE_API=mock npm run dev     # the mock, for offline UI work

   Every method takes an optional `RequestOptions` carrying an `AbortSignal`,
   because the Refresh control in the header must be able to cancel work in
   flight rather than racing it.
   ========================================================================= */

import type {
  AdminUser, CashNight, CashNightDraft, CashQuery, Comparison,
  CompareQuery, CompareRow, ConnectorHealth, Discrepancy, Distribution, Entity,
  Expense, ExpenseDraft, ExpensePage, ExpenseQuery, ExportFormat, Heatmap,
  HeatmapQuery, IsoDate, LedgerKind, LedgerMovement, Me, OccupancyQuery,
  OccupancyResponse, PaymentMethodRecord, Pulse, RangeQuery, Reimbursement,
  ReportDef, ReportRun, Series, SeriesQuery, Summary, ThemePreference, Venue,
  WeekdayBucket,
} from './types';
// ---- ask: the registry and the assistant. Kept as its own import so the
// list above stays exactly as the other three modules left it.
import type {
  AskAnswer, AskHistoryEntry, AskSchema, GrantScopeSpec, RegistryAuditEntry,
  RegistryGrant, RegistryUser, RegistryView, SavedGrants,
} from './types';
// ---- audit: the unified log. Kept as its own import so the list above
// stays exactly as the other modules left it.
import type { AuditPage, AuditQuery } from './types';


/* -------------------------------------------------------------- errors --- */

export type ApiErrorKind =
  | 'aborted'        // the caller cancelled — never render this as a failure
  | 'network'        // no answer at all
  | 'bad_request'
  | 'unauthorized'   // no session: send them to sign in
  | 'forbidden'      // signed in, not granted this module
  | 'not_found'
  | 'unavailable'    // 503 — the server is up but the database is not
  | 'server';

/**
 * The only error type screens have to handle. `kind` is what an error state
 * should branch on; `message` is safe to show.
 */
export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | null;
  /** Server-supplied detail, when there is any. */
  readonly detail: string | null;

  constructor(kind: ApiErrorKind, message: string, status: number | null = null, detail: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.detail = detail;
  }

  /** True when trying again could plausibly work. */
  get retryable(): boolean {
    return this.kind === 'network' || this.kind === 'unavailable' || this.kind === 'server';
  }

  /** Normalise anything thrown into an ApiError. */
  static from(e: unknown): ApiError {
    if (e instanceof ApiError) return e;
    if (e instanceof DOMException && e.name === 'AbortError') {
      return new ApiError('aborted', 'Request cancelled');
    }
    if (e instanceof Error) return new ApiError('network', e.message);
    // Screens and route guards raise `{ kind: 'forbidden' }` shapes; honour
    // them rather than flattening every one into a network failure.
    if (e && typeof e === 'object' && 'kind' in e) {
      const k = (e as { kind: ApiErrorKind }).kind;
      const msg = 'message' in e ? String((e as { message: unknown }).message) : k.replace(/_/g, ' ');
      return new ApiError(k, msg);
    }
    return new ApiError('network', 'Something went wrong');
  }
}

export interface RequestOptions {
  signal?: AbortSignal;
}

/* ------------------------------------------------------------ contract --- */

export interface Api {
  /* identity -------------------------------------------------------------- */
  me(o?: RequestOptions): Promise<Me>;
  setTheme(theme: ThemePreference, o?: RequestOptions): Promise<void>;
  logout(scope: 'device' | 'everywhere', o?: RequestOptions): Promise<void>;

  /* estate ---------------------------------------------------------------- */
  venues(o?: RequestOptions): Promise<Venue[]>;
  entities(o?: RequestOptions): Promise<Entity[]>;

  /* pulse ----------------------------------------------------------------- */
  pulse(o?: RequestOptions): Promise<Pulse>;

  /* sales ----------------------------------------------------------------- */
  summary(q: RangeQuery, o?: RequestOptions): Promise<Summary>;
  /** One entry when `splitByVenue` is false; one per venue when it is true. */
  series(q: SeriesQuery, o?: RequestOptions): Promise<Series[]>;
  heatmap(q: HeatmapQuery, o?: RequestOptions): Promise<Heatmap>;
  weekdays(q: RangeQuery, o?: RequestOptions): Promise<WeekdayBucket[]>;

  /* compare --------------------------------------------------------------- */
  compare(q: CompareQuery, o?: RequestOptions): Promise<Comparison>;
  /** Venue against venue over the same window. */
  compareVenues(q: CompareQuery, o?: RequestOptions): Promise<CompareRow[]>;

  /* expenses -------------------------------------------------------------- */
  expenses(q: ExpenseQuery, o?: RequestOptions): Promise<ExpensePage>;
  createExpense(draft: ExpenseDraft, o?: RequestOptions): Promise<Expense>;
  updateExpense(id: string, patch: Partial<ExpenseDraft>, o?: RequestOptions): Promise<Expense>;
  deleteExpense(id: string, o?: RequestOptions): Promise<void>;
  /** Cards and accounts, for matching a last-4 off a receipt. */
  paymentMethods(o?: RequestOptions): Promise<PaymentMethodRecord[]>;
  /** Out-of-pocket spend owed back, per person per entity. */
  reimbursements(q: { person?: string; entityId?: string; status?: Reimbursement['status'] }, o?: RequestOptions): Promise<Reimbursement[]>;

  /* cash & close ---------------------------------------------------------- */
  cashNights(q: CashQuery, o?: RequestOptions): Promise<CashNight[]>;
  saveCashNight(draft: CashNightDraft, o?: RequestOptions): Promise<CashNight>;
  ledger(q: CashQuery & { kind?: LedgerKind }, o?: RequestOptions): Promise<LedgerMovement[]>;
  distributions(q: CashQuery, o?: RequestOptions): Promise<Distribution[]>;
  discrepancies(q: CashQuery, o?: RequestOptions): Promise<Discrepancy[]>;

  /* occupancy ------------------------------------------------------------- */
  occupancy(q: OccupancyQuery, o?: RequestOptions): Promise<OccupancyResponse>;

  /* reports --------------------------------------------------------------- */
  reports(o?: RequestOptions): Promise<ReportDef[]>;
  runReport(defId: string, range: { from: IsoDate; to: IsoDate }, o?: RequestOptions): Promise<ReportRun>;
  /** Returns a URL to fetch; the export is a render of the screen, not a second code path. */
  exportReport(defId: string, format: ExportFormat, o?: RequestOptions): Promise<{ url: string; filename: string }>;

  /* admin ----------------------------------------------------------------- */
  connectors(o?: RequestOptions): Promise<ConnectorHealth[]>;
  adminUsers(o?: RequestOptions): Promise<AdminUser[]>;
  updateUser(id: string, patch: Partial<Pick<AdminUser, 'status' | 'isAdmin' | 'displayName'>>, o?: RequestOptions): Promise<AdminUser>;
  auditLog(q: AuditQuery, o?: RequestOptions): Promise<AuditPage>;
}

/* ------------------------------------------------------- HTTP client ----- */

const BASE = '/api';

function qs(params: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

async function request<T>(path: string, init: RequestInit & RequestOptions = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      // The session is a cookie set by the server behind the same origin, so
      // credentials must ride along; the dev proxy keeps the origin single.
      credentials: 'include',
      headers: { 'accept': 'application/json', ...(init.body ? { 'content-type': 'application/json' } : {}) },
      ...init,
    });
  } catch (e) {
    throw ApiError.from(e);
  }

  if (!res.ok) {
    // The server's error envelope is `{ error, message }`: `error` is a code
    // for branching (`unavailable`, `forbidden`) and `message` is the prose a
    // person has to act on — "no cash_ledger collection exists yet". Reading
    // the code into `detail` put the word "unavailable" on screen where the
    // reason belonged, which is barely better than the canned line it
    // replaced. Prefer the message; fall back to the code.
    let detail: string | null = null;
    try {
      const body: unknown = await res.json();
      if (body && typeof body === 'object') {
        const b = body as { error?: unknown; message?: unknown };
        if (typeof b.message === 'string' && b.message.trim()) detail = b.message;
        else if (b.error !== undefined) detail = String(b.error);
      }
    } catch { /* an error page that is not JSON is still an error */ }

    const kind: ApiErrorKind =
      res.status === 400 ? 'bad_request' :
      res.status === 401 ? 'unauthorized' :
      res.status === 403 ? 'forbidden' :
      res.status === 404 ? 'not_found' :
      res.status === 503 ? 'unavailable' : 'server';

    // A 503 always carries a reason, and the reason is the whole point:
    // "the database is down" and "no cash_ledger collection exists yet" want
    // different people to do different things. The canned line told every
    // reader the first one, which was usually the wrong one.
    const message =
      kind === 'unauthorized' ? 'Your session has ended. Sign in again.' :
      kind === 'forbidden' ? 'You do not have access to this.' :
      kind === 'unavailable' ? (detail ?? 'The data store is unreachable — figures are not being served.') :
      detail ?? `Request failed (${res.status})`;

    throw new ApiError(kind, message, res.status, detail);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const get = <T>(path: string, o?: RequestOptions) => request<T>(path, { method: 'GET', signal: o?.signal });
const send = <T>(method: string, path: string, body: unknown, o?: RequestOptions) =>
  request<T>(path, { method, body: JSON.stringify(body), signal: o?.signal });

/**
 * The real client. Every path here is the route the Axum server exposes (or
 * will): `/api/me` and `/api/summary` exist today, the rest are specified by
 * this file and land as the crates ship.
 */
export const httpApi: Api = {
  me: (o) => get<Me>('/me', o),
  setTheme: (theme, o) => send<void>('PUT', '/me/theme', { theme }, o),
  logout: (scope, o) => send<void>('POST', '/logout', { scope }, o),

  venues: (o) => get<Venue[]>('/venues', o),
  entities: (o) => get<Entity[]>('/entities', o),

  pulse: (o) => get<Pulse>('/pulse', o),

  summary: (q, o) => get<Summary>(`/summary${qs({ ...q })}`, o),
  series: (q, o) => get<Series[]>(`/series${qs({ ...q })}`, o),
  // The server nests the folding under `heatmap` on /heatmap (the old flat
  // shape never existed server-side — the endpoint refused before BUILD-L).
  // Lifted here, the one choke point every heat panel reads through; the
  // flat shape the mock serves is tolerated so dev never diverges.
  heatmap: (q, o) =>
    get<HourlyResponse>(`/heatmap${qs({ ...q })}`, o).then(
      (r) => (r as unknown as { heatmap?: Heatmap }).heatmap ?? (r as unknown as Heatmap),
    ),
  weekdays: (q, o) => get<WeekdayBucket[]>(`/weekdays${qs({ ...q })}`, o),

  compare: (q, o) => get<Comparison>(`/compare${qs({ ...q })}`, o),
  compareVenues: (q, o) => get<CompareRow[]>(`/compare/venues${qs({ ...q })}`, o),

  expenses: (q, o) => get<ExpensePage>(`/expenses${qs({ ...q })}`, o),
  createExpense: (draft, o) => send<Expense>('POST', '/expenses', draft, o),
  updateExpense: (id, patch, o) => send<Expense>('PATCH', `/expenses/${id}`, patch, o),
  deleteExpense: (id, o) => request<void>(`/expenses/${id}`, { method: 'DELETE', signal: o?.signal }),
  paymentMethods: (o) => get<PaymentMethodRecord[]>('/payment-methods', o),
  reimbursements: (q, o) => get<Reimbursement[]>(`/reimbursements${qs({ ...q })}`, o),

  cashNights: (q, o) => get<CashNight[]>(`/cash${qs({ ...q })}`, o),
  saveCashNight: (draft, o) => send<CashNight>('PUT', `/cash/${draft.venue}/${draft.date}`, draft, o),
  ledger: (q, o) => get<LedgerMovement[]>(`/cash/ledger${qs({ ...q })}`, o),
  distributions: (q, o) => get<Distribution[]>(`/cash/distributions${qs({ ...q })}`, o),
  discrepancies: (q, o) => get<Discrepancy[]>(`/reconciliation${qs({ ...q })}`, o),

  occupancy: (q, o) => get<OccupancyResponse>(`/occupancy${qs({ ...q })}`, o),

  reports: (o) => get<ReportDef[]>('/reports', o),
  runReport: (defId, range, o) => get<ReportRun>(`/reports/${defId}/run${qs({ ...range })}`, o),
  exportReport: (defId, format, o) =>
    get<{ url: string; filename: string }>(`/reports/${defId}/export${qs({ format })}`, o),

  connectors: (o) => get<ConnectorHealth[]>('/admin/connectors', o),
  adminUsers: (o) => get<AdminUser[]>('/admin/users', o),
  updateUser: (id, patch, o) => send<AdminUser>('PATCH', `/admin/users/${id}`, patch, o),
  auditLog: (q, o) => get<AuditPage>(`/admin/audit${qs({ ...q })}`, o),
};

/* ------------------------------------------------------- which one? ------ */

/**
 * **The real server is the default.** Set `VITE_API=mock` to work on the UI
 * offline; nothing else in the app changes either way.
 *
 * This used to be the other way round — the mock was the default and
 * `VITE_API=http` opted in. That was correct while `/api` was unpublished and
 * became a trap the moment it wasn't: a plain `npm run build`, which is what
 * anyone runs, shipped a production bundle full of invented figures naming the
 * real venues. It did exactly that once, and the dashboard read $434.90 for a
 * night that had actually taken $264,472 across the week.
 *
 * A wrong default that only bites in production is worth inverting rather than
 * remembering. Offline UI work is the rarer case, so it is the one that opts
 * in — and if someone forgets the flag there, they get real data, which is
 * merely inconvenient rather than wrong.
 */
export const USING_MOCK: boolean = import.meta.env.VITE_API === 'mock';

/* The mock is its own chunk, fetched on first use.
 *
 * It holds realistic-looking figures naming the real venues. They are
 * fabricated — and a fabricated number that looks authoritative is worse in
 * public than none at all — so it must not sit in the entry bundle, which
 * anyone can fetch before signing in.
 */
let mockImpl: Api | null = null;
const lazyMock = new Proxy({} as Api, {
  get(_t, prop: string) {
    return async (...args: unknown[]) => {
      if (!mockImpl) mockImpl = (await import('./mock')).mockApi;
      const fn = (mockImpl as unknown as Record<string, (...a: unknown[]) => unknown>)[prop];
      if (typeof fn !== 'function') throw new Error(`mock api has no method "${prop}"`);
      return fn.apply(mockImpl, args);
    };
  },
}) as Api;

export const api: Api = USING_MOCK ? lazyMock : httpApi;

/* ==========================================================================
   Parallel build regions.

   Four modules are being built at once, each by someone who owns exactly one
   routes file and one screen directory. They all need to extend this file,
   and appending at the bottom would make every one of them conflict with
   every other on the same lines.

   So each has a region below. **Write only inside your own markers, and never
   reformat, reorder or reflow anything outside them.** That way four sets of
   additions land in four places and git merges them without a human.

   If you need something in another module's region, it is a cross-module
   request: put it in docs/GRANT-CREDENTIAL-ISSUES.md and carry on without it.
   ========================================================================== */

/* ---- documents: contract ---- */

/* This region's own type imports — the block at the top of the file is
   outside it, and four modules appending to one import list is the merge
   these regions exist to prevent. Type-only, so it erases. */
import type {
  CompanyCreateInput, CompanyDetail, CompanyDrawer, CompanyPatchInput,
  CompanyRow, ConfiguredCompany, ConfirmFilingInput, CreatedCompany,
  DeletedCompany, DocumentDetail, DocumentId, DocumentText,
  FilingConfirmation, FilingReviewQueue, K1Query, K1View, RestoredCompany,
  UploadRecord, UploadedFile,
} from './types';

/**
 * The company drawer and the K-1 view.
 *
 * A second interface rather than methods on `Api`, because `Api` and its two
 * implementations are declared above this region and four modules extending
 * the same declaration at once is the merge this file's regions exist to
 * avoid. Screens import `documentsApi` and are otherwise unaffected.
 */
export interface DocumentsApi {
  /** The drawer list, scoped. A caller with no documents grant gets 403. */
  companies(o?: RequestOptions): Promise<CompanyRow[]>;
  /** Identity and the missing-document summary. */
  company(slug: string, o?: RequestOptions): Promise<CompanyDetail>;
  /** Every document, grouped into bands, with the gaps shown in place. */
  companyDocuments(slug: string, o?: RequestOptions): Promise<CompanyDrawer>;
  document(id: DocumentId, o?: RequestOptions): Promise<DocumentDetail>;
  /** The stored OCR text, to copy. 503 where the document has none. */
  text(id: DocumentId, o?: RequestOptions): Promise<DocumentText>;
  k1s(q: K1Query, o?: RequestOptions): Promise<K1View>;
  /** Filings waiting on a human, and only what this caller may act on. */
  review(o?: RequestOptions): Promise<FilingReviewQueue>;
  /** Record a filing decision. Audited per item; the queue clears on rebuild. */
  confirmFiling(id: DocumentId, body: ConfirmFilingInput, o?: RequestOptions): Promise<FilingConfirmation>;
  /**
   * File a new company. Org-superuser only; the drawer opens empty with a
   * full gap report, and nobody — including the creator — is granted anything.
   */
  createCompany(input: CompanyCreateInput, o?: RequestOptions): Promise<CreatedCompany>;
  /**
   * Edit the company record. Per-entity admin or superuser; audited
   * before/after, and the archive itself is never rewritten.
   */
  configureCompany(slug: string, patch: CompanyPatchInput, o?: RequestOptions): Promise<ConfiguredCompany>;
  /**
   * Delete a company by typing its slug. Org-superuser only; documents are
   * re-filed to `unfiled`, never removed, and K-1 links are kept.
   */
  deleteCompany(slug: string, confirm: string, o?: RequestOptions): Promise<DeletedCompany>;
  /** Bring a deleted company back inside the undo window. */
  restoreCompany(slug: string, o?: RequestOptions): Promise<RestoredCompany>;
  /** Where every upload for one company stands, live from the queue. */
  uploads(slug: string, o?: RequestOptions): Promise<UploadRecord[]>;
}

/**
 * File one upload's bytes.
 *
 * Raw bytes, one file per request — no multipart framing, so the bytes
 * hashed server-side are byte-for-byte the bytes stored. The filename rides
 * in the query; the server keeps the final segment only.
 */
export async function uploadCompanyFile(
  slug: string,
  filename: string,
  file: Blob,
  opts: { k1?: boolean; docType?: string } & RequestOptions = {},
): Promise<UploadedFile> {
  const kind = opts.k1 ? 'k1s' : 'documents';
  const path = `/companies/${encodeURIComponent(slug)}/${kind}/upload` +
    `?filename=${encodeURIComponent(filename)}` +
    (opts.docType ? `&docType=${encodeURIComponent(opts.docType)}` : '');
  return request<UploadedFile>(path, {
    method: 'POST',
    body: file,
    headers: { accept: 'application/json', 'content-type': 'application/octet-stream' },
    signal: opts.signal,
  });
}

/**
 * Where the bytes are.
 *
 * Not a `fetch` — the browser has to do this one itself so the file lands in
 * the downloads folder rather than in a `Blob` in the tab's memory. Some of
 * these are 160 MB. The server streams it, sets `Content-Disposition`, and
 * writes an audit row before the first byte moves.
 */
export function documentDownloadUrl(id: DocumentId): string {
  return `${BASE}/documents/${encodeURIComponent(id)}/download`;
}

/**
 * Where the bytes are for looking at rather than keeping.
 *
 * The same rule as the download: not a `fetch` — the browser navigates an
 * `<iframe>` or an `<img>` at this URL itself, so a 60 MB scan streams
 * through the renderer (the server answers `inline` with single-range 206)
 * instead of landing in a `Blob` in the tab's memory. Served through the
 * same gate as the download, with the audit row saying `view` before the
 * first byte moves.
 */
export function documentViewUrl(id: DocumentId): string {
  return `${BASE}/documents/${encodeURIComponent(id)}/view`;
}

const httpDocumentsApi: DocumentsApi = {
  companies: (o) => get<CompanyRow[]>('/companies', o),
  company: (slug, o) => get<CompanyDetail>(`/companies/${encodeURIComponent(slug)}`, o),
  companyDocuments: (slug, o) =>
    get<CompanyDrawer>(`/companies/${encodeURIComponent(slug)}/documents`, o),
  document: (id, o) => get<DocumentDetail>(`/documents/${encodeURIComponent(id)}`, o),
  text: (id, o) => get<DocumentText>(`/documents/${encodeURIComponent(id)}/text`, o),
  k1s: (q, o) => get<K1View>(`/k1s${qs({ ...q })}`, o),
  review: (o) => get<FilingReviewQueue>('/filing/review', o),
  confirmFiling: (id, body, o) =>
    send<FilingConfirmation>('POST', `/filing/review/${encodeURIComponent(id)}/confirm`, body, o),
  createCompany: (input, o) => send<CreatedCompany>('POST', '/companies', input, o),
  configureCompany: (slug, patch, o) =>
    send<ConfiguredCompany>('PATCH', `/companies/${encodeURIComponent(slug)}`, patch, o),
  deleteCompany: (slug, confirm, o) =>
    send<DeletedCompany>('DELETE', `/companies/${encodeURIComponent(slug)}`, { confirm }, o),
  restoreCompany: (slug, o) =>
    send<RestoredCompany>('POST', `/companies/${encodeURIComponent(slug)}/restore`, null, o),
  uploads: (slug, o) => get<UploadRecord[]>(`/companies/${encodeURIComponent(slug)}/uploads`, o),
};

/** The mock, fetched on first use, for the same reason `lazyMock` is. */
const lazyDocumentsMock = new Proxy({} as DocumentsApi, {
  get(_t, prop: string) {
    return async (...args: unknown[]) => {
      const impl = (await import('./mock')).mockDocumentsApi as unknown as
        Record<string, (...a: unknown[]) => unknown>;
      const fn = impl[prop];
      if (typeof fn !== 'function') throw new Error(`mock documents api has no method "${prop}"`);
      return fn.apply(impl, args);
    };
  },
}) as DocumentsApi;

export const documentsApi: DocumentsApi = USING_MOCK ? lazyDocumentsMock : httpDocumentsApi;

/* ---- end documents: contract ---- */


/* ---- expenses: contract ---- */
/* Four routes the six `Api` methods above do not cover.
 *
 * Standalone exports rather than members of `Api`, deliberately. Adding a
 * member to that interface means adding an implementation to `httpApi` *and*
 * to `mockApi`, and both live outside this region — so the one change that is
 * supposed to land in one place would land in three, two of them in files
 * three other people are editing right now.
 *
 * They call the module-private `request` directly, so they get the same error
 * envelope, the same `ApiError` kinds and the same credentialled fetch as
 * everything else in this file. Nothing here re-implements that.
 */

import type { ReceiptProposal, UploadedPage } from './types';

/**
 * Send one photographed page, before an expense exists.
 *
 * Deliberately before: the photograph is taken before the company has been
 * chosen, and making the upload wait for the form puts the slowest part of
 * capture in front of the fastest. The reply is the page's content hash,
 * which is the whole of its address — the same photograph sent twice is one
 * file, and that is *not* the same guarantee as the capture key. Sameness of
 * bytes is knowable; sameness of intent is not.
 *
 * The body is the raw bytes. `request` spreads `init` last, so the headers
 * given here replace the JSON ones rather than merging with them.
 */
export async function uploadReceiptPage(file: Blob, o?: RequestOptions): Promise<UploadedPage> {
  if (USING_MOCK) {
    throw new ApiError(
      'unavailable',
      'The mock holds no receipt store, so a page cannot be uploaded. Run against the real server to attach photographs.',
    );
  }
  return request<UploadedPage>('/receipts', {
    method: 'POST',
    body: file,
    // An empty `type` is normal for HEIC in some browsers. The server sniffs
    // the bytes and only falls back to this claim for PDFs, so sending what
    // the browser said is right and sending nothing is survivable.
    headers: { accept: 'application/json', 'content-type': file.type || 'application/octet-stream' },
    signal: o?.signal,
  });
}

/**
 * Ask the reader what the pages say. **Returns a proposal and writes nothing.**
 *
 * A 503 here is a reader that is not configured or not answering, and it must
 * stay distinguishable from a read that came back empty: "we could not read
 * it" and "there was nothing on it" want different things from the person
 * holding the receipt.
 */
export async function readReceiptPages(imageIds: string[], o?: RequestOptions): Promise<ReceiptProposal> {
  if (USING_MOCK) {
    throw new ApiError('unavailable', 'The mock does not read receipts.');
  }
  return send<ReceiptProposal>('POST', '/receipts/read', { imageIds }, o);
}

/** Hand a reimbursement claim to whoever pays it. */
export function submitReimbursement(id: string, o?: RequestOptions): Promise<Reimbursement> {
  return send<Reimbursement>('POST', `/reimbursements/${id}/submit`, {}, o);
}

/**
 * Record that a claim was paid, and take its receipts out of "owed back".
 *
 * `paidOn` is the date the money actually moved, which is not the date
 * somebody pressed the button; `null` lets the server use today.
 */
export function settleReimbursement(
  id: string,
  paidOn: IsoDate | null,
  o?: RequestOptions,
): Promise<Reimbursement> {
  return send<Reimbursement>('POST', `/reimbursements/${id}/settle`, { paidOn }, o);
}
/* ---- end expenses: contract ---- */

/* ---- aircraft: contract ---- */

import type {
  AircraftCalendar, AircraftCompliance, AircraftLegDraft, AircraftLegs,
  AircraftPartners, AircraftProgrammeView, AircraftReconciliation, AircraftRecord,
} from './types';

/**
 * N278SC. A separate client rather than methods on `Api`, because the `Api`
 * interface is outside this module's region and four modules extending it at
 * once is the merge this file's regions exist to prevent.
 *
 * Same rules as `httpApi`: cookie credentials, the shared `request` helper, and
 * an `ApiError` whose `kind` is what a screen branches on. A 503 here always
 * carries the reason — "the flight log is not readable on this machine" and
 * "the PlaneRelated calendar is not cached" want different people to do
 * different things, and neither is an aeroplane that has not flown.
 */
export interface AircraftApi {
  /** The register. One aeroplane; the CJ4 in the CAMP account is not in it. */
  fleet(o?: RequestOptions): Promise<AircraftRecord[]>;
  legs(tail: string, q?: { year?: number; partner?: string; flagged?: boolean }, o?: RequestOptions): Promise<AircraftLegs>;
  /**
   * File a leg. Rejects with `ApiError` — `bad_request` for a cell that will
   * not read, and a 422 carrying the continuity flags for a leg whose airframe
   * hours do not follow from the one before it.
   */
  fileLeg(tail: string, draft: AircraftLegDraft, o?: RequestOptions): Promise<void>;
  partners(tail: string, year: number, o?: RequestOptions): Promise<AircraftPartners>;
  calendar(tail: string, q?: { year?: number; needsReview?: boolean }, o?: RequestOptions): Promise<AircraftCalendar>;
  compliance(tail: string, o?: RequestOptions): Promise<AircraftCompliance>;
  reconciliation(tail: string, year?: number, o?: RequestOptions): Promise<AircraftReconciliation>;
  programme(tail: string, o?: RequestOptions): Promise<AircraftProgrammeView>;
}

const httpAircraft: AircraftApi = {
  fleet: (o) => get<AircraftRecord[]>('/aircraft', o),
  legs: (tail, q, o) => get<AircraftLegs>(`/aircraft/${encodeURIComponent(tail)}/legs${qs({ ...q })}`, o),
  fileLeg: (tail, draft, o) => send<void>('POST', `/aircraft/${encodeURIComponent(tail)}/legs`, draft, o),
  partners: (tail, year, o) => get<AircraftPartners>(`/aircraft/${encodeURIComponent(tail)}/partners${qs({ year })}`, o),
  calendar: (tail, q, o) => get<AircraftCalendar>(`/aircraft/${encodeURIComponent(tail)}/calendar${qs({ ...q })}`, o),
  compliance: (tail, o) => get<AircraftCompliance>(`/aircraft/${encodeURIComponent(tail)}/compliance`, o),
  reconciliation: (tail, year, o) => get<AircraftReconciliation>(`/aircraft/${encodeURIComponent(tail)}/reconciliation${qs({ year })}`, o),
  programme: (tail, o) => get<AircraftProgrammeView>(`/aircraft/${encodeURIComponent(tail)}/programme`, o),
};

/* The mock is lazy for the same reason the main one is: it holds figures that
   look authoritative and are invented, and they must not sit in the entry
   bundle where anyone can fetch them before signing in. */
let mockAircraftImpl: AircraftApi | null = null;
const lazyMockAircraft = new Proxy({} as AircraftApi, {
  get(_t, prop: string) {
    return async (...args: unknown[]) => {
      if (!mockAircraftImpl) mockAircraftImpl = (await import('./mock')).mockAircraftApi;
      const fn = (mockAircraftImpl as unknown as Record<string, (...a: unknown[]) => unknown>)[prop];
      if (typeof fn !== 'function') throw new Error(`mock aircraft api has no method "${prop}"`);
      return fn.apply(mockAircraftImpl, args);
    };
  },
}) as AircraftApi;

export const aircraftApi: AircraftApi = USING_MOCK ? lazyMockAircraft : httpAircraft;

/** The one aeroplane. Keyed on the tail because it is what a pilot says. */
export const N278SC = 'N278SC';

/* ---- end aircraft: contract ---- */
/* ---- aircraft-ops: contract ---- */

import type {
  OpsAirport, OpsBlockEstimate, OpsCalendar, OpsFieldDetail, OpsNearbyField,
  OpsOutboxEntry, OpsReservation, OpsReservationRequest, OpsCorrection,
  OpsReviewQueue, OpsWeather, OpsWindAloft,
} from './types';

/**
 * Aircraft ops (BUILD-H): calendar tab, reservations, fields, weather.
 * Same rules as `aircraftApi`: cookie credentials, the shared `request`
 * helper, `ApiError.kind` for branching. Mounted beside the aircraft routes
 * at `/api/aircraft/ops/...`; needs the two-line mount in `lib.rs`/`routes.rs`
 * (cross-module request — the lane that owns those files applies it).
 */
export interface AircraftOpsApi {
  calendar(tail: string, q?: { year?: number; month?: number }, o?: RequestOptions): Promise<OpsCalendar>;
  review(tail: string, o?: RequestOptions): Promise<OpsReviewQueue>;
  correct(tail: string, key: string, correction: OpsCorrection, o?: RequestOptions): Promise<void>;
  airports(q: string, o?: RequestOptions): Promise<{ query: string; airports: OpsAirport[] }>;
  nearby(origin: string, o?: RequestOptions): Promise<{ origin: string; fields: OpsNearbyField[] }>;
  field(ident: string, o?: RequestOptions): Promise<OpsFieldDetail>;
  weather(field: string, o?: RequestOptions): Promise<OpsWeather>;
  blockTime(args: { from: string; to: string; winds?: OpsWindAloft[]; modelCycle?: string }, o?: RequestOptions): Promise<OpsBlockEstimate>;
  reservations(tail: string, o?: RequestOptions): Promise<{ reservations: OpsReservation[] }>;
  requestTrip(draft: OpsReservationRequest, o?: RequestOptions): Promise<{ reservation: OpsReservation; outbox: OpsOutboxEntry[] }>;
  approve(id: string, o?: RequestOptions): Promise<OpsReservation>;
  decline(id: string, reason: string, o?: RequestOptions): Promise<OpsReservation>;
  assign(id: string, pilot: string, o?: RequestOptions): Promise<OpsReservation>;
  cancelReservation(id: string, reason: string, o?: RequestOptions): Promise<OpsReservation>;
  toDraft(id: string, o?: RequestOptions): Promise<OpsReservation>;
  outbox(reservationId: string, o?: RequestOptions): Promise<{ entries: OpsOutboxEntry[] }>;
}

const OPS = '/aircraft/ops';

const httpAircraftOps: AircraftOpsApi = {
  calendar: (tail, q, o) => get<OpsCalendar>(`${OPS}/calendar${qs({ tail, ...q })}`, o),
  review: (tail, o) => get<OpsReviewQueue>(`${OPS}/review${qs({ tail })}`, o),
  correct: (tail, key, correction, o) => send<void>('POST', `${OPS}/review/${encodeURIComponent(key)}${qs({ tail })}`, correction, o),
  airports: (q, o) => get<{ query: string; airports: OpsAirport[] }>(`${OPS}/airports${qs({ q })}`, o),
  nearby: (origin, o) => send<{ origin: string; fields: OpsNearbyField[] }>('POST', `${OPS}/fields/nearby`, { origin }, o),
  field: (ident, o) => get<OpsFieldDetail>(`${OPS}/fields/${encodeURIComponent(ident)}`, o),
  weather: (field, o) => get<OpsWeather>(`${OPS}/weather${qs({ field })}`, o),
  blockTime: (args, o) => send<OpsBlockEstimate>('POST', `${OPS}/block-time`, args, o),
  reservations: (tail, o) => get<{ reservations: OpsReservation[] }>(`${OPS}/reservations${qs({ tail })}`, o),
  requestTrip: (draft, o) => send<{ reservation: OpsReservation; outbox: OpsOutboxEntry[] }>('POST', `${OPS}/reservations`, draft, o),
  approve: (id, o) => send<OpsReservation>('POST', `${OPS}/reservations/${encodeURIComponent(id)}/approve`, {}, o),
  decline: (id, reason, o) => send<OpsReservation>('POST', `${OPS}/reservations/${encodeURIComponent(id)}/decline`, { reason }, o),
  assign: (id, pilot, o) => send<OpsReservation>('POST', `${OPS}/reservations/${encodeURIComponent(id)}/assign`, { pilot }, o),
  cancelReservation: (id, reason, o) => send<OpsReservation>('POST', `${OPS}/reservations/${encodeURIComponent(id)}/cancel`, { reason }, o),
  toDraft: (id, o) => send<OpsReservation>('POST', `${OPS}/reservations/${encodeURIComponent(id)}/draft`, {}, o),
  outbox: (reservationId, o) => get<{ entries: OpsOutboxEntry[] }>(`${OPS}/outbox${qs({ reservation: reservationId })}`, o),
};

let mockAircraftOpsImpl: AircraftOpsApi | null = null;
const lazyMockAircraftOps = new Proxy({} as AircraftOpsApi, {
  get(_t, prop: string) {
    return async (...args: unknown[]) => {
      if (!mockAircraftOpsImpl) mockAircraftOpsImpl = (await import('./mock')).mockAircraftOpsApi;
      const fn = (mockAircraftOpsImpl as unknown as Record<string, (...a: unknown[]) => unknown>)[prop];
      if (typeof fn !== 'function') throw new Error(`mock aircraft-ops api has no method "${prop}"`);
      return fn.apply(mockAircraftOpsImpl, args);
    };
  },
}) as AircraftOpsApi;

export const aircraftOpsApi: AircraftOpsApi = USING_MOCK ? lazyMockAircraftOps : httpAircraftOps;

/* ---- end aircraft-ops: contract ---- */

/* ---- calcache: contract ---- */

import type { CalcacheRefreshRequest, CalcacheStatus } from './types';

/**
 * Calendar cache (work order Q). Standalone functions rather than methods on
 * `aircraftApi`: that interface is another region's, and several regions
 * extending one object is the merge this file's regions exist to prevent.
 *
 * Refresh is a pull-request, not a fetch: POST only records the request and
 * ingest205 honours it on its next pass, minutes away. Poll
 * `aircraftCalendarCacheStatus` until `refresh.honored`.
 */
export async function requestAircraftCalendarRefresh(
  tail: string, o?: RequestOptions,
): Promise<CalcacheRefreshRequest> {
  if (USING_MOCK) return (await import('./mock')).mockCalcacheApi.requestRefresh(tail);
  return request<CalcacheRefreshRequest>(`/aircraft/${encodeURIComponent(tail)}/calendar`, {
    method: 'POST', signal: o?.signal,
  });
}

export async function aircraftCalendarCacheStatus(
  tail: string, o?: RequestOptions,
): Promise<CalcacheStatus> {
  if (USING_MOCK) return (await import('./mock')).mockCalcacheApi.cacheStatus(tail);
  return get<CalcacheStatus>(`/aircraft/${encodeURIComponent(tail)}/calendar/refresh`, o);
}

/* ---- end calcache: contract ---- */

/* ---- ask: contract ---- */

/**
 * The registry and the assistant.
 *
 * Two things to know about this block.
 *
 * **It is not on the `Api` interface, and it has no mock.** Everything else in
 * this file has a fabricated twin so the UI can be worked on offline. An
 * assistant cannot: a fabricated answer that names real venues and quotes a
 * figure is precisely the artefact this module exists to prevent, and one has
 * already been shipped once from this file — the dashboard read $434.90 for a
 * night that had actually taken $264,472. So `/ask` talks to the server or it
 * says it cannot.
 *
 * **The paths are `/admin/registry/…`, not `/admin/users/…`.** The shorter
 * ones are still mounted as refusals by a routes file that four parallel
 * modules were told not to touch, and axum panics on a duplicate route. The
 * longer name is also the truer one: this is the registry that used to be a
 * file.
 */
export const registryApi = {
  /** Everyone, their grants, the companies, and the role templates. */
  view: (o?: RequestOptions) => get<RegistryView>('/admin/registry', o),

  /** Replace one person's grants with an explicit set. */
  saveGrants: (userId: string, grants: RegistryGrant[], o?: RequestOptions) =>
    send<SavedGrants>('PUT', `/admin/registry/users/${userId}/grants`, { grants }, o),

  /**
   * Apply a role at one scope. The server expands it into explicit rows and
   * returns them, so what comes back is what authorization will actually
   * evaluate — nothing stores "GM".
   */
  applyTemplate: (userId: string, template: string, scope: GrantScopeSpec, o?: RequestOptions) =>
    send<SavedGrants>('PUT', `/admin/registry/users/${userId}/grants`, { template, scope }, o),

  patchUser: (
    userId: string,
    patch: { displayName?: string; status?: 'active' | 'disabled'; isAdmin?: boolean },
    o?: RequestOptions,
  ) => send<RegistryUser>('PATCH', `/admin/registry/users/${userId}`, patch, o),

  audit: (limit = 80, o?: RequestOptions) =>
    get<RegistryAuditEntry[]>(`/admin/registry/audit${qs({ limit })}`, o),
};

export const askApi = {
  ask: (question: string, o?: RequestOptions) =>
    send<AskAnswer>('POST', '/ask', { question }, o),
  schema: (o?: RequestOptions) => get<AskSchema>('/ask/schema', o),
  history: (limit = 30, o?: RequestOptions) =>
    get<AskHistoryEntry[]>(`/ask/history${qs({ limit })}`, o),
};

/* ---- end ask: contract ---- */

/* ---- occupancy: contract ---- */
/* Standalone exports rather than members of `Api`, deliberately — the same
 * reason the expenses region states: extending that interface means touching
 * `httpApi` and `mockApi` outside this region. The documents-lane shape
 * (interface, HTTP implementation, lazy mock proxy, one export) is followed
 * instead, so this region is self-contained.
 */

import type {
  OccupancyHistoryResponse, OccupancyLiveResponse,
  OccupancyVenuesResponse, VenueFilter,
} from './types';

export interface OccupancyApi {
  /** History with hourly buckets, door-vs-POS variance and gaps. */
  history(q: OccupancyQuery, o?: RequestOptions): Promise<OccupancyHistoryResponse>;
  /** Current headcount per counted venue, with freshness badges. */
  live(q: { venue?: VenueFilter }, o?: RequestOptions): Promise<OccupancyLiveResponse>;
  /** The served topology, plus whether the poller has written counts. */
  venues(o?: RequestOptions): Promise<OccupancyVenuesResponse>;
}

const httpOccupancyApi: OccupancyApi = {
  history: (q, o) => get<OccupancyHistoryResponse>(`/occupancy${qs({ ...q })}`, o),
  live: (q, o) => get<OccupancyLiveResponse>(`/occupancy/live${qs({ ...q })}`, o),
  venues: (o) => get<OccupancyVenuesResponse>('/occupancy/venues', o),
};

/** The mock, fetched on first use, for the same reason `lazyMock` is. */
const lazyOccupancyMock = new Proxy({} as OccupancyApi, {
  get(_t, prop: string) {
    return async (...args: unknown[]) => {
      const impl = (await import('./mock')).mockOccupancyApi as unknown as
        Record<string, (...a: unknown[]) => unknown>;
      const fn = impl[prop];
      if (typeof fn !== 'function') throw new Error(`mock occupancy api has no method "${prop}"`);
      return fn.apply(impl, args);
    };
  },
}) as OccupancyApi;

export const occupancyApi: OccupancyApi = USING_MOCK ? lazyOccupancyMock : httpOccupancyApi;
/* ---- end occupancy: contract ---- */

/* ---- cash: contract ---- */
/* (nothing yet - see docs/BUILD-*.md) */
/* ---- end cash: contract ---- */

/* ---- reports: contract ---- */
/**
 * Where an export's bytes are.
 *
 * The export endpoint streams the file itself, with a `Content-Disposition`
 * attachment — so the screen anchors straight at it and the browser saves it,
 * rather than fetching bytes into the tab's memory. This is the DocumentSheet
 * principle: several of these renders are large, and a Blob in the tab is the
 * wrong place for them. `Api.exportReport`'s `{ url, filename }` payload
 * describes what the mock returns, not what this route serves.
 */
export function reportExportUrl(defId: string, format: ExportFormat): string {
  return `${BASE}/reports/${encodeURIComponent(defId)}/export${qs({ format })}`;
}
/* ---- end reports: contract ---- */

/* ---- lineleap: contract ---- */

/* Rounders ticket revenue, reconciled against Stripe payouts.
 *
 * A separate client rather than methods on `Api`, because `Api` and its two
 * implementations are declared above every region and every module extending
 * the same declaration at once is the merge these regions exist to avoid.
 * Screens import `ticketsApi` and are otherwise unaffected.
 *
 * Same rules as `httpApi`: cookie credentials, the shared `request` helper,
 * and an `ApiError` whose `kind` is what a screen branches on. A 403 names
 * nothing — not venues, not nights, not even which source is connected. A
 * 503 always carries the reason: "no database", "no pull has landed yet",
 * "Two Dimes has no credential" want different people to do different
 * things. Two Dimes refuses by name — never 200 with `[]`, which would claim
 * it sold no tickets.
 */

import type {
  TicketsPage, VenueTickets,
} from './types';

export interface TicketsApi {
  /** Rounders ticket revenue, scoped, paginated. `venue` narrows within the
   * caller's scope and never widens it; the Two Dimes names refuse by name. */
  nights(
    q?: { venue?: string; from?: IsoDate; to?: IsoDate; page?: number; perPage?: number },
    o?: RequestOptions,
  ): Promise<TicketsPage>;
  /** Per-venue night detail plus the payouts that settle it. */
  venue(
    venue: string,
    q?: { from?: IsoDate; to?: IsoDate; page?: number; perPage?: number },
    o?: RequestOptions,
  ): Promise<VenueTickets>;
}

const httpTicketsApi: TicketsApi = {
  nights: (q, o) => get<TicketsPage>(`/tickets${qs({ ...q })}`, o),
  venue: (venue, q, o) =>
    get<VenueTickets>(`/tickets/${encodeURIComponent(venue)}${qs({ ...q })}`, o),
};

/** The mock, fetched on first use, for the same reason `lazyMock` is. */
const lazyTicketsMock = new Proxy({} as TicketsApi, {
  get(_t, prop: string) {
    return async (...args: unknown[]) => {
      const impl = (await import('./mock')).mockTicketsApi as unknown as
        Record<string, (...a: unknown[]) => unknown>;
      const fn = impl[prop];
      if (typeof fn !== 'function') throw new Error(`mock tickets api has no method "${prop}"`);
      return fn.apply(impl, args);
    };
  },
}) as TicketsApi;

export const ticketsApi: TicketsApi = USING_MOCK ? lazyTicketsMock : httpTicketsApi;

/* ---- end lineleap: contract ---- */

/* ---- abc: contract ---- */

// Alabama ABC ordering + beverage cost: pricelist months, order history,
// per-item beverage cost with refusals in the cells, per-venue source status.
//
// A separate client rather than methods on 'Api', because 'Api' and its two
// implementations are declared above every region and every module extending
// the same declaration at once is the merge these regions exist to avoid.
// Screens import 'abcApi' and are otherwise unaffected.
//
// Same rules as 'httpApi': cookie credentials, the shared 'request' helper,
// and an 'ApiError' whose 'kind' is what a screen branches on. A 403 names
// nothing - not venues, not months, not even which ABC sources exist. A 503
// always carries the reason: no database, no month captured yet, Two Dimes
// has no credential. Two Dimes refuses by name - never 200 with an empty
// list, which would claim we looked at Two Dimes ordering and found none.

import type {
  AbcBeveragePage, AbcOrderPage, AbcPricelistPage,
} from './types';

export interface AbcApi {
  // The month's items, paginated. 503 with the fetch address when the
  // month was never captured.
  pricelist(
    q?: { month?: string; page?: number; perPage?: number },
    o?: RequestOptions,
  ): Promise<AbcPricelistPage>;
  // Order history, scoped, newest last.
  orders(
    q?: { entity?: string; from?: IsoDate; to?: IsoDate; page?: number; perPage?: number },
    o?: RequestOptions,
  ): Promise<AbcOrderPage>;
  // Per-item beverage cost plus pour-cost refusals, by venue.
  beverageCost(
    q?: { entity?: string; from?: IsoDate; to?: IsoDate; pourMl?: number },
    o?: RequestOptions,
  ): Promise<AbcBeveragePage>;
  // Per-venue source status, Two Dimes refusal-by-name included.
  sources(o?: RequestOptions): Promise<ConnectorHealth[]>;
}

const httpAbcApi: AbcApi = {
  pricelist: (q, o) => get<AbcPricelistPage>('/abc/pricelist' + qs({ ...q }), o),
  orders: (q, o) => get<AbcOrderPage>('/abc/orders' + qs({ ...q }), o),
  beverageCost: (q, o) => get<AbcBeveragePage>('/abc/beverage-cost' + qs({ ...q }), o),
  sources: (o) => get('/abc/sources', o),
};

// The mock, fetched on first use, for the same reason 'lazyMock' is.
const lazyAbcMock = new Proxy({} as AbcApi, {
  get(_t, prop: string) {
    return async (...args: unknown[]) => {
      const impl = (await import('./mock')).mockAbcApi as unknown as
        Record<string, (...a: unknown[]) => unknown>;
      const fn = impl[prop];
      if (typeof fn !== 'function') throw new Error('mock abc api has no such method');
      return fn.apply(impl, args);
    };
  },
}) as AbcApi;

export const abcApi: AbcApi = USING_MOCK ? lazyAbcMock : httpAbcApi;

/* ---- end abc: contract ---- */



/* ---- ledger: contract ---- */

import type {
  JournalDraft, JournalPage, LedgerBalanceSheet, LedgerCashFlow, LedgerChart,
  LedgerEntry, LedgerStatementType, ProfitLoss, TrialBalance,
} from './types';

/**
 * The general ledger. A dedicated client rather than methods on `Api`: the
 * books are per-entity and opt-in, and nothing here is venue-shaped.
 */
export const ledgerApi = {
  /** The entity's chart of accounts. Refuses — never an empty chart — where
   * the entity keeps no ledger. */
  chart: (entity: string, o?: RequestOptions) =>
    get<LedgerChart>(`/ledger/accounts${qs({ entity })}`, o),

  /** Postings, newest last, paginated. */
  journal: (
    q: { entity: string; from?: string; to?: string; account?: string; limit?: number; page?: number },
    o?: RequestOptions,
  ) => get<JournalPage>(`/ledger/journal${qs({ ...q })}`, o),

  /** Post one balanced entry. An unbalanced draft is refused with its
   * difference (422) — never rounded, never coerced. */
  postEntry: (draft: JournalDraft, o?: RequestOptions) =>
    send<LedgerEntry>('POST', '/ledger/journal', draft, o),

  /** Debits, credits and balances per account. Explains itself when empty. */
  trialBalance: (q: { entity: string; as_of?: string }, o?: RequestOptions) =>
    get<TrialBalance>(`/ledger/trial-balance${qs({ ...q })}`, o),

  /** One of the three statements. `from` and `to` are required: a statement
   * must name its window. */
  statements: (
    q: { type: LedgerStatementType; entity: string; from: string; to: string },
    o?: RequestOptions,
  ) =>
    get<ProfitLoss | LedgerBalanceSheet | LedgerCashFlow>(
      `/ledger/statements${qs({ ...q })}`, o,
    ),

  /** Sign the chart off. Admin-gated server-side (`Capability::Approve`) --
   * this client makes no capability decision of its own, the same way
   * `aparApi.approveBill` does not either. Refused with a reason for an
   * empty chart, or for anyone without that capability. */
  approveChart: (entity: string, o?: RequestOptions) =>
    send<LedgerChart>('POST', '/ledger/chart/approve', { entity }, o),
};

/* ---- end ledger: contract ---- */

/* ---- tracking: contract ---- */
/* ---- tracking: contract (work order M) ---- */

import type { TrackPosition, TrackQuery, TrackWindow } from './types';

/**
 * N278SC live position + flown track. A separate client rather than methods
 * on `Api`, because the `Api` interface is outside this module's region and
 * four modules extending it at once is the merge this file's regions exist
 * to prevent.
 *
 * Same rules as `httpApi`: cookie credentials, the shared `request` helper,
 * and an `ApiError` whose `kind` is what a screen branches on. A 403 names
 * nothing — not the aeroplane, not its hex, not where it was. A 503 always
 * carries the reason: "the ADS-B feed is unreachable" and "no database on
 * this machine" want different people to do different things, and neither
 * is an aeroplane that has not flown.
 */
export interface TrackingApi {
  /** The current fix with its freshness. Unknown is a 503, never a state. */
  position(tail: string, o?: RequestOptions): Promise<TrackPosition>;
  /** Flown samples over the window, oldest first, paginated. */
  track(tail: string, q?: TrackQuery, o?: RequestOptions): Promise<TrackWindow>;
}

const httpTracking: TrackingApi = {
  position: (tail, o) =>
    get<TrackPosition>(`/aircraft/${encodeURIComponent(tail)}/position`, o),
  track: (tail, q, o) =>
    get<TrackWindow>(`/aircraft/${encodeURIComponent(tail)}/track${qs({ ...q })}`, o),
};

/* The mock is lazy for the same reason the main one is: it holds figures
   that look authoritative and are invented, and they must not sit in the
   entry bundle where anyone can fetch them before signing in. */
let mockTrackingImpl: TrackingApi | null = null;
const lazyMockTracking = new Proxy({} as TrackingApi, {
  get(_t, prop: string) {
    return async (...args: unknown[]) => {
      if (!mockTrackingImpl) mockTrackingImpl = (await import('./mock')).mockTrackingApi;
      const fn = (mockTrackingImpl as unknown as Record<string, (...a: unknown[]) => unknown>)[prop];
      if (typeof fn !== 'function') throw new Error(`mock tracking api has no method "${prop}"`);
      return fn.apply(mockTrackingImpl, args);
    };
  },
}) as TrackingApi;

export const trackingApi: TrackingApi = USING_MOCK ? lazyMockTracking : httpTracking;

/* ---- end tracking: contract ---- */
/* ---- banking: contract ---- */
/* Read-only balances, transactions and reconciliation.
 *
 * A separate client rather than methods on `Api`, because `Api` and its two
 * implementations are declared above every region and every module extending
 * the same declaration at once is the merge these regions exist to avoid.
 * Screens import `bankingApi` and are otherwise unaffected.
 *
 * Same rules as `httpApi`: cookie credentials, the shared `request` helper,
 * and an `ApiError` whose `kind` is what a screen branches on. A 403 names
 * nothing — not account names, not balances, not even which institutions
 * are connected. A 503 always carries the reason: "no database", "Bank
 * First has no account list", "no pull has succeeded yet" want different
 * people to do different things.
 */

import type {
  BankAccount, BankAccountDetail, BankFeed, BankInstitution, BankMatchInput,
  BankMatchResult, BankRuleInput, BankRuleResult, BankTransaction,
  ReconQueue,
} from './types';

export interface BankingApi {
  /** Balances, scoped, each with feed freshness. */
  accounts(
    q?: { institution?: BankInstitution; unmapped?: boolean },
    o?: RequestOptions,
  ): Promise<BankAccount[]>;
  /** One account + its freshness + mapping state. */
  account(id: string, o?: RequestOptions): Promise<BankAccountDetail>;
  /** Assert which company holds an account, or clear it with null. */
  setMapping(id: string, entityId: string | null, o?: RequestOptions): Promise<BankAccount>;
  /** Posted transactions; state = matched | unmatched | awaiting | all. */
  transactions(
    q?: { account?: string; from?: string; to?: string; state?: string; institution?: BankInstitution },
    o?: RequestOptions,
  ): Promise<BankTransaction[]>;
  /** The queue: unmatched first, then awaiting-confirm. */
  queue(q?: { institution?: BankInstitution }, o?: RequestOptions): Promise<ReconQueue>;
  /** Confirm a suggested match or assert a manual one. Moves no money. */
  confirmMatch(body: BankMatchInput, o?: RequestOptions): Promise<BankMatchResult>;
  /** Reopen a match. The undo is itself recorded. */
  undoMatch(txnId: string, o?: RequestOptions): Promise<BankMatchResult>;
  /** Store a memoize rule; hits file as awaiting-confirm, never auto. */
  createRule(body: BankRuleInput, o?: RequestOptions): Promise<BankRuleResult>;
  /** Per-institution feed health — the freshness screen. */
  feeds(o?: RequestOptions): Promise<BankFeed[]>;
}

const httpBankingApi: BankingApi = {
  accounts: (q, o) => get<BankAccount[]>(`/banking/accounts${qs({ ...q })}`, o),
  account: (id, o) => get<BankAccountDetail>(`/banking/accounts/${encodeURIComponent(id)}`, o),
  setMapping: (id, entityId, o) =>
    send<BankAccount>('POST', `/banking/accounts/${encodeURIComponent(id)}/mapping`, { entityId }, o),
  transactions: (q, o) => get<BankTransaction[]>(`/banking/transactions${qs({ ...q })}`, o),
  queue: (q, o) => get<ReconQueue>(`/banking/reconciliation${qs({ ...q })}`, o),
  confirmMatch: (body, o) => send<BankMatchResult>('POST', '/banking/reconciliation/match', body, o),
  undoMatch: (txnId, o) =>
    send<BankMatchResult>('POST', '/banking/reconciliation/match', { txnId, undo: true }, o),
  createRule: (body, o) => send<BankRuleResult>('POST', '/banking/mapping-rules', body, o),
  feeds: (o) => get<BankFeed[]>('/banking/feeds', o),
};

/** The mock, fetched on first use, for the same reason `lazyMock` is. */
const lazyBankingMock = new Proxy({} as BankingApi, {
  get(_t, prop: string) {
    return async (...args: unknown[]) => {
      const impl = (await import('./mock')).mockBankingApi as unknown as
        Record<string, (...a: unknown[]) => unknown>;
      const fn = impl[prop];
      if (typeof fn !== 'function') throw new Error(`mock banking api has no method "${prop}"`);
      return fn.apply(impl, args);
    };
  },
}) as BankingApi;

export const bankingApi: BankingApi = USING_MOCK ? lazyBankingMock : httpBankingApi;

/* ---- end banking: contract ---- */
/* ---- banking-amex: contract ---- */
/* AMEX reads ride the banking contract above with
 * `q.institution = 'amex'` — accounts, transactions, queue and feeds
 * all scope by institution already, so no new methods. Unavailable
 * before the first good pull surfaces as a 503 naming the AMEX feed,
 * never 200 with [].
 */
/* ---- end banking-amex: contract ---- */
/* ---- hourly: contract ---- */

import type { Basis, HourlyResponse } from './types';

/**
 * Per-hour buckets plus the heatmap folding, served by one handler behind
 * both /api/hourly and /api/heatmap (BUILD-L).
 *
 * A separate client rather than methods on `Api`: `Api` and its two
 * implementations are declared above every region, and every module extending
 * the same declaration at once is the merge these regions exist to avoid.
 */
export interface HourlyApi {
  /** Per-hour buckets, oldest first. Pass rowKind date for date rows. */
  hourly(
    q: { from: string; to: string; venue?: VenueFilter; basis?: Basis; rowKind?: 'weekday' | 'date' },
    o?: RequestOptions,
  ): Promise<HourlyResponse>;
  /** The same buckets folded weekday x hour. */
  heatmap(
    q: { from: string; to: string; venue?: VenueFilter; basis?: Basis; rowKind?: 'weekday' | 'date' },
    o?: RequestOptions,
  ): Promise<HourlyResponse>;
}

export const httpHourlyApi: HourlyApi = {
  hourly: (q, o) => get<HourlyResponse>(`/hourly${qs({ ...q })}`, o),
  heatmap: (q, o) => get<HourlyResponse>(`/heatmap${qs({ ...q })}`, o),
};

const lazyHourlyMock = new Proxy({} as HourlyApi, {
  get(_t, prop: string) {
    return async (...args: unknown[]) => {
      const impl = (await import('./mock')).mockHourlyApi as unknown as
        Record<string, (...a: unknown[]) => unknown>;
      const fn = impl[prop];
      if (typeof fn !== 'function') throw new Error(`mock hourly api has no method "${prop}"`);
      return fn.apply(impl, args);
    };
  },
}) as HourlyApi;

export const hourlyApi: HourlyApi = USING_MOCK ? lazyHourlyMock : httpHourlyApi;

/* ---- end hourly: contract ---- */
/* ---- adp: contract ---- */
/* ADP payroll reads plus the admin repull.
 *
 * A separate client rather than methods on `Api`, because `Api` and its two
 * implementations are declared above every region and every module extending
 * the same declaration at once is the merge these regions exist to avoid.
 * Screens import `payrollApi` and are otherwise unaffected.
 *
 * Same rules as `httpApi`: cookie credentials, the shared `request` helper,
 * and an `ApiError` whose `kind` is what a screen branches on. A 403 names
 * nothing — not venues, not weeks, not even which source is missing. A 503
 * always carries the reason: "no database", "no ADP pull has landed", the
 * Restaurant365 refusal and the MFA-unresolved repull refusal want different
 * people to do different things.
 */

import type {
  PayrollRepull, PayrollRuns, PayrollSummary,
} from './types';

export interface PayrollQuery {
  entity?: string;
  from?: IsoDate;
  to?: IsoDate;
  page?: number;
  perPage?: number;
}

export interface PayrollApi {
  /** Gross pay, employer taxes, fees and headcount per venue per week. */
  summary(q?: PayrollQuery, o?: RequestOptions): Promise<PayrollSummary>;
  /** Individual payroll runs, oldest first. */
  runs(q?: PayrollQuery, o?: RequestOptions): Promise<PayrollRuns>;
  /** Record a repull run. Refuses (503) until the probe lands a session. */
  repull(o?: RequestOptions): Promise<PayrollRepull[]>;
}

const httpPayrollApi: PayrollApi = {
  summary: (q, o) => get<PayrollSummary>(`/payroll/summary${qs({ ...q })}`, o),
  runs: (q, o) => get<PayrollRuns>(`/payroll/runs${qs({ ...q })}`, o),
  repull: (o) => send<PayrollRepull[]>('POST', '/admin/connectors/adp/repull', {}, o),
};

/** The mock, fetched on first use, for the same reason `lazyMock` is. */
const lazyPayrollMock = new Proxy({} as PayrollApi, {
  get(_t, prop: string) {
    return async (...args: unknown[]) => {
      const impl = (await import('./mock')).mockPayrollApi as unknown as
        Record<string, (...a: unknown[]) => unknown>;
      const fn = impl[prop];
      if (typeof fn !== 'function') throw new Error(`mock payroll api has no method "${prop}"`);
      return fn.apply(impl, args);
    };
  },
}) as PayrollApi;

export const payrollApi: PayrollApi = USING_MOCK ? lazyPayrollMock : httpPayrollApi;

/* ---- end adp: contract ---- */

/* ---- ingest-retry: contract ---- */
/* Breaker status + manual reset for the ingest retry lane (work order S).
 *
 * Standalone `RetryApi` rather than members of `Api`, deliberately — the
 * same reason the expenses lane documents above: the interface, the HTTP
 * implementation and the mock all land in this one region, in a file three
 * other lanes are editing right now.
 *
 * They call the module-private `get`/`send` directly, so they get the same
 * error envelope, the same `ApiError` kinds and the same credentialled
 * fetch as everything else in this file.
 */

import type { CircuitStatus, ResetResult } from './types';

/** The retry lane's half of the Sources screen contract. */
export interface RetryApi {
  /** One row per visible venue: the breaker's door, its cause, next probe. */
  circuits(o?: RequestOptions): Promise<CircuitStatus[]>;
  /**
   * Clear a venue's breaker now (`smarttab-rounders`). Forces one attempt
   * on the next worker pass; declares nothing about health — a failed
   * attempt re-opens by the normal rules.
   */
  resetCircuit(id: string, o?: RequestOptions): Promise<ResetResult>;
}

const httpRetryApi: RetryApi = {
  circuits: (o) => get<CircuitStatus[]>('/admin/ingest/circuits', o),
  resetCircuit: (id, o) => send<ResetResult>('POST', `/admin/connectors/${id}/reset`, {}, o),
};

/** The mock, fetched on first use, for the same reason `lazyMock` is. */
const lazyRetryMock = new Proxy({} as RetryApi, {
  get(_t, prop: string) {
    return async (...args: unknown[]) => {
      const impl = (await import('./mock')).mockRetryApi as unknown as
        Record<string, (...a: unknown[]) => unknown>;
      const fn = impl[prop];
      if (typeof fn !== 'function') throw new Error(`mock retry api has no method "${prop}"`);
      return fn.apply(impl, args);
    };
  },
}) as RetryApi;

export const retryApi: RetryApi = USING_MOCK ? lazyRetryMock : httpRetryApi;

/* ---- end ingest-retry: contract ---- */
/* ---- salestax: contract ---- */

import type {
  CalendarEntry, RateDraft, RegistrationDraft, SalesTaxPack, TaxRate,
  TaxRegistration, Worksheet,
} from './types';

/**
 * Sales-tax filings. A dedicated client rather than methods on `Api`: the
 * module is per-entity and opt-in, and nothing here is venue-shaped.
 *
 * There is no submit-to-the-state route and none is ordered: `recordFiling`
 * records a human's filing rather than performing one.
 */
export const salestaxApi = {
  /** Registration rows in scope. Refuses — never an empty list — where the module is absent. */
  registrations: (entity: string, o?: RequestOptions) =>
    get<TaxRegistration[]>(`/salestax/registrations${qs({ entity })}`, o),

  /** Upsert a registration. Grant supplies the content. */
  upsertRegistration: (draft: RegistrationDraft, o?: RequestOptions) =>
    send<TaxRegistration>('POST', '/salestax/registrations', draft, o),

  /** Rate versions in force as of a date, one per jurisdiction.
   *
   * The param is named as_of, not asOf: qs() sends object keys verbatim on
   * the wire with no camelCase-to-snake_case translation anywhere in this
   * client, and RatesQuery::as_of in salestax_routes.rs is a plain field
   * with no #[serde(rename)]. An asOf key here is simply an unrecognized
   * query param that axum silently drops -- never filed against a real
   * date, never a 400. Found unused (the screen calls rates(undefined, o)
   * today) before anyone wired up a date picker against it. */
  rates: (q?: { jurisdiction?: string; as_of?: string }, o?: RequestOptions) =>
    get<TaxRate[]>(`/salestax/rates${qs({ ...q })}`, o),

  /** Append a rate version. History is never edited: a version on an
   * existing day is refused, and corrections arrive as new versions. */
  postRate: (draft: RateDraft, o?: RequestOptions) =>
    send<TaxRate>('POST', '/salestax/rates', draft, o),

  /** The period worksheet from POS tax rows. Uncomputable cells refuse in
   * place — a worksheet with no POS rows is not a worksheet of zeros. */
  worksheet: (
    q: { entity: string; from: string; to: string },
    o?: RequestOptions,
  ) => get<Worksheet>(`/salestax/worksheet${qs({ ...q })}`, o),

  /** Return packets and their statuses. */
  packs: (
    q: { entity: string; period?: string; jurisdiction?: string },
    o?: RequestOptions,
  ) => get<SalesTaxPack[]>(`/salestax/packs${qs({ ...q })}`, o),

  /** Freeze a worksheet month into a draft PACK. Drafts rebuild in place;
   * confirmed and filed PACKs correct through amendments. */
  buildPack: (
    draft: { entity: string; period: string; jurisdiction: string; amends?: string; note?: string },
    o?: RequestOptions,
  ) => send<SalesTaxPack>('POST', '/salestax/packs', draft, o),

  /** The human review sign-off. Locks the numbers; files nothing. */
  confirmPack: (
    id: string,
    body: { note?: string; deltaDisposition?: string },
    o?: RequestOptions,
  ) => send<SalesTaxPack>('POST', `/salestax/packs/${encodeURIComponent(id)}/confirm`, body, o),

  /** The after-the-fact truth: filed outside the app, with confirmation
   * number, filed date, filed by. Requires the in-app review first. */
  recordFiling: (
    id: string,
    body: { confirmationNumber: string; filedDate: string; filedBy: string; note?: string },
    o?: RequestOptions,
  ) => send<SalesTaxPack>('POST', `/salestax/packs/${encodeURIComponent(id)}/record-filing`, body, o),

  /** Due dates with statuses over the window. The T-7 / T-1 / overdue
   * surfacing reads `daysUntilDue` — there is no mailer here. */
  calendar: (
    q: { entity: string; from: string; to: string },
    o?: RequestOptions,
  ) => get<CalendarEntry[]>(`/salestax/calendar${qs({ ...q })}`, o),
};

/* ---- end salestax: contract ---- */

/* ---- budget: contract ---- */

import type {
  BudgetDetail, BudgetVariance, BudgetVersion, CashForecast,
} from './types';

/**
 * Season-aware budgets, variance with reasons, and the 13-week forecast.
 *
 * A dedicated client rather than methods on `Api`: the books are per-entity
 * and opt-in, and nothing here is venue-shaped. Like the ledger client, this
 * one is direct — there is no mock budget, because a fabricated variance or
 * forecast is exactly the artefact this module exists to prevent. The screen
 * talks to the server or reports that it cannot, in every mode.
 *
 * Same rules as `httpApi`: cookie credentials, the shared `request` helper,
 * and an `ApiError` whose `kind` is what a screen branches on. A 403 names
 * nothing — not companies, not versions, not weeks. A 503 always carries the
 * reason: "no database", "no current version", "APAR has not landed" want
 * different people to do different things.
 */
export const budgetApi = {
  /** Versions, newest first (current + superseded). */
  versions: (
    q: { entity?: string; year?: number },
    o?: RequestOptions,
  ) => get<BudgetVersion[]>(`/budgets${qs({ ...q })}`, o),

  /** Record a version: supersede, never edit. Corrections are new versions
   * with a `note` saying why. Recording needs Enter on the ledger module. */
  createVersion: (
    body: {
      entity: string; year: number; note: string; materiality?: string;
      lines: Array<{ month: string; accountCode: string; amount: string; basis?: string }>;
      annotations?: Array<{ month: string; accountCode: string; note: string }>;
    },
    o?: RequestOptions,
  ) => send<BudgetDetail>('POST', '/budgets', body, o),

  /** One version with its lines. Superseded versions read back byte-identical. */
  version: (id: string, o?: RequestOptions) =>
    get<BudgetDetail>(`/budgets/${encodeURIComponent(id)}`, o),

  /** The current version's lines for one month. Refuses for an entity with
   * no budget — never an empty line list, which would claim a plan exists. */
  current: (
    q: { entity?: string; month: string },
    o?: RequestOptions,
  ) => get<BudgetDetail>(`/budgets/current${qs({ ...q })}`, o),

  /** Budget vs actual with reasons over the window. `version` pins the
   * explanation to the version it was written against. */
  variance: (
    q: { entity?: string; from: string; to: string; version?: string },
    o?: RequestOptions,
  ) => get<BudgetVariance>(`/budgets/variance${qs({ ...q })}`, o),

  /** The 13-week forecast (cap 26). Each leg carries its basis; legs with no
   * source refuse in their cells and name it. */
  forecast: (
    q: { entity?: string; weeks?: number; as_of?: string },
    o?: RequestOptions,
  ) => get<CashForecast>(`/cashflow/forecast${qs({ ...q })}`, o),
};

/* ---- end budget: contract ---- */
/* ---- apar: contract ---- */

import type {
  AparAging, AparBill, AparBillsPage, AparBillDraft, AparInvoice, AparInvoiceDraft,
  AparInvoicesPage, AparPayRun, AparStatement,
} from './types';

/**
 * Accounts payable and receivable. A dedicated client rather than methods
 * on `Api`, because `Api` and its two implementations are declared above
 * every region and every module extending the same declaration at once is
 * the merge these regions exist to avoid. Screens import `aparApi` and are
 * otherwise unaffected.
 *
 * Same rules as `httpApi`: cookie credentials, the shared `request`
 * helper, and an `ApiError` whose `kind` is what a screen branches on. A
 * 403 names nothing — not vendors, not bills, not even which companies
 * exist. A 503 always carries the reason: no database, or the company keeps
 * no ledger. Refusals name the missing module — never 200 with `[]`, which
 * would claim the company owes nothing.
 *
 * No mock client, like the ledger: figures against real companies are not
 * fabricated. Every method talks to the server or reports that it cannot,
 * in every mode including `empty`.
 */
export const aparApi = {
  /** Bills, newest last, paginated. */
  bills: (
    q: { entity?: string; state?: string; vendor?: string; from?: string; to?: string; limit?: number; page?: number },
    o?: RequestOptions,
  ) => get<AparBillsPage>(`/ap/bills${qs({ ...q })}`, o),

  /** Enter a bill: draft, from an OCR claim or by hand. Posts nothing. */
  enterBill: (draft: AparBillDraft, o?: RequestOptions) =>
    send<AparBill>('POST', '/ap/bills', draft, o),

  /** Approve: takes Approve, posts the balanced entry. Idempotent. */
  approveBill: (
    id: string, body: { entity: string; payablesAccount: string; note?: string }, o?: RequestOptions,
  ) => send<AparBill>('POST', `/ap/bills/${encodeURIComponent(id)}/approve`, body, o),

  /** Void: takes Approve. Drafts void quietly; approved bills reverse. */
  voidBill: (
    id: string, body: { entity: string; note?: string }, o?: RequestOptions,
  ) => send<AparBill>('POST', `/ap/bills/${encodeURIComponent(id)}/void`, body, o),

  /** Pay runs with their state. */
  payRuns: (
    q: { entity?: string; state?: string }, o?: RequestOptions,
  ) => get<AparPayRun[]>(`/ap/pay-runs${qs({ ...q })}`, o),

  /** Build a run from approved, unpaid, due bills. */
  buildPayRun: (
    body: { entity: string; billIds?: string[]; dueThrough?: string }, o?: RequestOptions,
  ) => send<AparPayRun>('POST', '/ap/pay-runs', body, o),

  /**
   * The remittance file. A download, not a transmission: CSV served with a
   * content-disposition attachment. There is no endpoint here that submits,
   * schedules or approves a transfer at a bank.
   */
  exportPayRun: async (
    id: string, q: { entity?: string }, o?: RequestOptions,
  ): Promise<{ filename: string; text: string }> => {
    let res: Response;
    try {
      res = await fetch(`/api/ap/pay-runs/${encodeURIComponent(id)}/export${qs({ ...q })}`, {
        credentials: 'include',
        headers: { accept: 'text/csv' },
        signal: o?.signal,
      });
    } catch (e) {
      throw ApiError.from(e);
    }
    if (!res.ok) {
      let detail: string | null = null;
      try {
        const body: unknown = await res.json();
        if (body && typeof body === 'object') {
          const b = body as { error?: unknown; message?: unknown };
          if (typeof b.message === 'string' && b.message.trim()) detail = b.message;
          else if (b.error !== undefined) detail = String(b.error);
        }
      } catch { /* an error page that is not JSON is still an error */ }
      const kind: ApiErrorKind =
        res.status === 400 ? 'bad_request' :
        res.status === 403 ? 'forbidden' :
        res.status === 404 ? 'not_found' :
        res.status === 503 ? 'unavailable' : 'server';
      throw new ApiError(kind, detail ?? `Export failed (${res.status})`, res.status, detail);
    }
    const disposition = res.headers.get('content-disposition') ?? '';
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `pay-run-${id}.csv`;
    return { filename, text: await res.text() };
  },

  /**
   * Mark paid: human attestation after the money moved outside. Records
   * whose hands moved it — never moves it.
   */
  markPayRunPaid: (
    id: string,
    body: { entity: string; cashAccount: string; movedBy: string; paidOn?: string; reference?: string },
    o?: RequestOptions,
  ) => send<AparPayRun>('POST', `/ap/pay-runs/${encodeURIComponent(id)}/mark-paid`, body, o),

  /** Invoices, paginated. */
  invoices: (
    q: { entity?: string; state?: string; customer?: string; overdue?: boolean; as_of?: string; limit?: number; page?: number },
    o?: RequestOptions,
  ) => get<AparInvoicesPage>(`/ar/invoices${qs({ ...q })}`, o),

  /** Enter an invoice: draft, by hand or from the owner-tab import. */
  enterInvoice: (draft: AparInvoiceDraft, o?: RequestOptions) =>
    send<AparInvoice>('POST', '/ar/invoices', draft, o),

  /** Send: takes Approve, assigns the number, posts the receivable. */
  sendInvoice: (
    id: string, body: { entity: string; receivablesAccount: string; note?: string }, o?: RequestOptions,
  ) => send<AparInvoice>('POST', `/ar/invoices/${encodeURIComponent(id)}/send`, body, o),

  /** Payment received, full or partial. Overpayments refuse; re-recording
   * the same reference is a lookup, not a double. */
  recordPayment: (
    id: string,
    body: { entity: string; cashAccount: string; amount: string; receivedOn?: string; method?: string; reference: string },
    o?: RequestOptions,
  ) => send<AparInvoice>('POST', `/ar/invoices/${encodeURIComponent(id)}/record-payment`, body, o),

  /** Void: takes Approve. Sent invoices with no payments reverse. */
  voidInvoice: (
    id: string, body: { entity: string; note?: string }, o?: RequestOptions,
  ) => send<AparInvoice>('POST', `/ar/invoices/${encodeURIComponent(id)}/void`, body, o),

  /** Buckets per customer as of a date, with invoice detail. */
  aging: (
    q: { entity?: string; as_of?: string }, o?: RequestOptions,
  ) => get<AparAging>(`/ar/aging${qs({ ...q })}`, o),

  /** One customer's invoices, payments and balance as of a date. */
  statement: (
    q: { customer: string; as_of?: string }, o?: RequestOptions,
  ) => get<AparStatement>(`/ar/statements${qs({ ...q })}`, o),
};

/* ---- end apar: contract ---- */
/* ---- syncdesk: contract ---- */
/* Source-status console + operator controls (work order U).
 *
 * Standalone `SyncDeskApi` rather than members of `Api`, deliberately — the
 * same reason the retry lane documents above: the interface, the HTTP
 * implementation and the mock all land in this one region, in a file three
 * other lanes are editing right now.
 */

import type {
  AlertEmailResult, CadenceResult, Syncdesk, SyncRequestResult,
} from './types';

/** The SyncDesk half of the Admin contract. */
export interface SyncDeskApi {
  /** Every source the caller may see, one row each, plus the posture strip. */
  overview(o?: RequestOptions): Promise<Syncdesk>;
  /**
   * Push-sync-now for one source. Answers what it did (requested / ran) —
   * never "synced". A repeat inside the per-source interval is 429 with
   * the retry-after instant.
   */
  syncNow(id: string, o?: RequestOptions): Promise<SyncRequestResult>;
  /** Set a source's pull cadence, in seconds. 422 outside its range. */
  setCadence(id: string, intervalSeconds: number, o?: RequestOptions): Promise<CadenceResult>;
  /** Set a source's stall-alert address; null clears. 422 for non-tenant. */
  setAlertEmail(id: string, email: string | null, o?: RequestOptions): Promise<AlertEmailResult>;
}

const httpSyncDeskApi: SyncDeskApi = {
  overview: (o) => get<Syncdesk>('/admin/syncdesk', o),
  syncNow: (id, o) => send<SyncRequestResult>('POST', `/admin/syncdesk/${id}/sync`, {}, o),
  setCadence: (id, intervalSeconds, o) =>
    send<CadenceResult>('PUT', `/admin/syncdesk/${id}/cadence`, { intervalSeconds }, o),
  setAlertEmail: (id, email, o) =>
    send<AlertEmailResult>('PUT', `/admin/syncdesk/${id}/alert-email`, { email }, o),
};

/** The mock, fetched on first use, for the same reason `lazyMock` is. */
const lazySyncDeskMock = new Proxy({} as SyncDeskApi, {
  get(_t, prop: string) {
    return async (...args: unknown[]) => {
      const impl = (await import('./mock')).mockSyncDeskApi as unknown as
        Record<string, (...a: unknown[]) => unknown>;
      const fn = impl[prop];
      if (typeof fn !== 'function') throw new Error(`mock syncdesk api has no method "${prop}"`);
      return fn.apply(impl, args);
    };
  },
}) as SyncDeskApi;

export const syncDeskApi: SyncDeskApi = USING_MOCK ? lazySyncDeskMock : httpSyncDeskApi;

/* ---- end syncdesk: contract ---- */


/* ---- lineleap dashboard: client ---- */

import type { LineLeapDashboard } from './types';

export const lineleapApi = {
  /** Every LineLeap day in the caller's scope (optionally windowed), with the
   * owner-sheet figures for the same nights. A 503 carries the reason. */
  dashboard: (q: { venue?: string; from?: IsoDate; to?: IsoDate } = {}, o?: RequestOptions) =>
    get<LineLeapDashboard>(`/lineleap/dashboard${qs({ ...q })}`, o),
};
